import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {
    ApplicationChangeEvent,
    AppModuleChangeEvent,
    AppReportChangeEvent,
    IUIApplication
} from "../PA.UIFramework.Core/PA.UIFramework.IF.Aspects";
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {
    WB_ReportTileRow,
    WB_UIAppExecutableRow,
    WB_UIApplicationRow, WB_UIAppModuleRow, WB_UIAppReportRow,
} from "../PA.Core/PAdbSchemaRows";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {
    esWB_ReportTile,
    esWB_UI_App_Report,
    esWB_UI_AppExecutable,
    esWB_UI_Application,
    esWB_UI_AppModule
} from "../PA.Core/PAdbSchema";
import {ASync} from "../PA.Core/Basic/ASync";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import _ = require('lodash');
import {BehaviorSubject} from "rxjs";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {UIApplicationBOValidation} from "./UIApplicationBO.Validation";
import {RecordUtils} from "../PA.Core/Basic/Records";


export class UIApplicationBO implements IBusinessObject, IUIApplication {

    Identity: IdentityProvider;
    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_UIApplication: WB_UIApplicationRow;

    dctAuditedDeletes: { [id: number]: number }

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Application'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_UIApplication = undefined;

        this.IUIApplication_init()

    }

    get WB_UIApplication(): WB_UIApplicationRow {
        return this.drWB_UIApplication;
    }


    //<editor-fold desc = "IBusinessObject operations">


    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: UIApplicationBO = this;

        const tgt = new UIApplicationBO({});
        await tgt.LoadNew()

        if (!tgt.bIsLoaded)
            throw new Error("Unable to create New Application")

        RecordUtils.CopyProperties(self.dbContext, esWB_UI_Application,
            src.WB_UIApplication, tgt.WB_UIApplication,
            [],
            ['DELETED_DT', 'DELETED_IND'],
            {})

        tgt.WB_UIApplication.cif.APP_ID = src.WB_UIApplication.cif.APP_ID + "_Copy"
        tgt.WB_UIApplication.cif.APP_NAME = src.WB_UIApplication.cif.APP_NAME + "_Copy"
        tgt.Identity.setAll(tgt.WB_UIApplication.cif.WB_UI_APP_ID, tgt.WB_UIApplication.cif.APP_ID, tgt.WB_UIApplication.cif.APP_NAME);

        for (let aRow of src.WB_UIApplication.WB_UIAppModuleRows) {
            if (!aRow.isActive)
                continue
            let appModuleRow = await tgt.addModule([aRow.cif.WB_UI_APPEXEC_ID])
            appModuleRow.cif.APPMODULE_NAME = aRow.cif.APPMODULE_NAME
            appModuleRow.cif.APPMODULE_SHORTNAME = aRow.cif.APPMODULE_SHORTNAME
            appModuleRow.cif.KEYWORDS = aRow.cif.KEYWORDS
            appModuleRow.cif.COMMENTS = aRow.cif.COMMENTS
            appModuleRow.cif.EDIT_ROLES = aRow.cif.EDIT_ROLES
            appModuleRow.cif.VIEW_ROLES = aRow.cif.VIEW_ROLES
            appModuleRow.cif.EXEC_PARAMETERS = aRow.cif.EXEC_PARAMETERS
            appModuleRow.cif.UI5_ICON_COLOR = aRow.cif.UI5_ICON_COLOR
            appModuleRow.cif.UI5_ICON = aRow.cif.UI5_ICON
            appModuleRow.cif.LAUNCH_POLICY = aRow.cif.LAUNCH_POLICY
            appModuleRow.cif.MRU_POLICY = aRow.cif.MRU_POLICY
            appModuleRow.cif.PLACEMENT_POLICY = aRow.cif.PLACEMENT_POLICY
            appModuleRow.cif.STARTUP_POLICY = aRow.cif.STARTUP_POLICY
            appModuleRow.cif.RENDER_OPTIONS = aRow.cif.RENDER_OPTIONS
        }

        for (let aRow of src.WB_UIApplication.WB_UIAppReportRows) {
            if (!aRow.isActive)
                continue
            let appReportRow = await tgt.addReport([aRow.cif.WB_REPORTTILE_ID])
            RecordUtils.CopyProperties(self.dbContext, esWB_ReportTile, aRow, appReportRow, [], [], {})
        }

        return tgt
    }

    LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Application");

        let internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (_.isEmpty(self.dbContext)) {
                var sError = "Unable to determine dbContext for new Application.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_UI_Application)
                .then((newApp: WB_UIApplicationRow) => {
                    newApp.setDefaults()
                    self.drWB_UIApplication = newApp;
                    self.drWB_UIApplication.cif.APP_ID = ""
                    self.drWB_UIApplication.cif.APP_NAME = "New Application"
                    self.dbContext.add(esWB_UI_Application, newApp)
                    self.Identity.setAll(self.WB_UIApplication.cif.WB_UI_APP_ID, self.WB_UIApplication.cif.APP_ID, self.WB_UIApplication.cif.APP_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_UIApplication, 'WB_UI_APP_ID', 'APP_ID', 'APP_NAME')
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        self._OnLoadComplete = internalOnComplete

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Application already loaded.")
                resolve(self); // "Application already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, {})


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Application.")
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = UIApplicationBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = UIApplicationBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let anExecRow: any = recordResolver.get(esWB_UI_Application, anId)

                    if (_.isEmpty(anExecRow)) {
                        error("Application not Found");
                        return;
                    }

                    self.drWB_UIApplication = anExecRow;
                    self.Identity.setAll(self.WB_UIApplication.cif.WB_UI_APP_ID, self.WB_UIApplication.cif.APP_ID, self.WB_UIApplication.cif.APP_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_UIApplication, 'WB_UI_APP_ID', 'APP_ID', 'APP_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_UIApplication.cif.APP_NAME + "(" + self.WB_UIApplication.cif.APP_ID + ")");

        self.ValidateOptions = options || {};

        if (_.isEmpty(self.dbContext)) {
            self._bIsValidated = false;
            const sError = "Unable to determine dbContext for to Validate Application.";
            self.ValidateProgressProvider.exception(sError);
            throw sError
        }

        await UIApplicationBOValidation.RunValidations(self)

        if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
            self._bIsValidated = false;
        } else {
            self._bIsValidated = true;
        }
        self.ValidateProgressProvider.finish();
        return self

    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }


    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        self.SaveProgressProvider.start("Saving " + self.WB_UIApplication.cif.APP_NAME + "(" + self.WB_UIApplication.cif.APP_ID + ")");
        self._bIsSaved = false;

        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // -- We could search the tree if the there is any record where store isn't set.

        if (_.isEmpty(self.dbContext)) {
            const sError = "Unable to determine dbContext to Save Application.";
            self.SaveProgressProvider.exception(sError);
            throw sError
        }

        self.SaveOptions = options || {};
        try {
            await self.Validate({})
            if(!self.bIsValidated)
                return self

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            await saveFunction(esWB_UI_Application, self.drWB_UIApplication, {})

            const tasks: Promise<any>[] = []
            tasks.push(saveFunction(esWB_UI_App_Report, self.drWB_UIApplication.WB_UIAppReportRows))
            tasks.push(saveFunction(esWB_UI_AppModule, self.drWB_UIApplication.WB_UIAppModuleRows))
            await Promise.all(tasks)
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete------------------------------------------------------------------------
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;

        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + self.WB_UIApplication.cif.APP_NAME + "(" + self.WB_UIApplication.cif.APP_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Application

            // 2. If Valid, mark all the records for deletion.
            self.drWB_UIApplication.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "IRecordResolver Operations">

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_UI_Application, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {
                [esWB_UI_Application]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_UI_App_Report]: {"DELETED_IND": {"notIn": ['1']}},
            },
            dctIncludeBelongsToNamedRelations: {}
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver;

    }

    //</editor-fold>


    //<editor-fold desc = "IModel">


    private _ApplicationPropertyChanged: BehaviorSubject<ApplicationChangeEvent>;
    private _AppReportPropertyChanged: BehaviorSubject<AppReportChangeEvent>;
    private _AppReportCollectionChanged: BehaviorSubject<IUIApplication>;
    private _AppModulePropertyChanged: BehaviorSubject<AppModuleChangeEvent>;
    private _AppModuleCollectionChanged: BehaviorSubject<IUIApplication>;


    private IUIApplication_init() {
        this._ApplicationPropertyChanged = new BehaviorSubject<ApplicationChangeEvent>(undefined)
        this._AppReportPropertyChanged = new BehaviorSubject<AppReportChangeEvent>(undefined)
        this._AppReportCollectionChanged = new BehaviorSubject<IUIApplication>(undefined)
        this._AppModulePropertyChanged = new BehaviorSubject<AppModuleChangeEvent>(undefined)
        this._AppModuleCollectionChanged = new BehaviorSubject<IUIApplication>(undefined)
    }


    private IUIApplication_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.WB_UIApplication.setupOnChangeEvents([], self.IUIApplication_onUIApplicationPRecordChange.bind(self), self)

            _.forEach(self.WB_UIApplication.WB_UIAppReportRows, (aRow) => {
                if (!aRow.isActive) {
                    return
                }
                aRow.setupOnChangeEvents([], self.IUIApplication_onAppReportRecordChange.bind(self), self)
            })
            _.forEach(self.WB_UIApplication.WB_UIAppModuleRows, (aRow) => {
                if (!aRow.isActive) {
                    return
                }
                aRow.setupOnChangeEvents([], self.IUIApplication_onAppModuleRecordChange.bind(self), self)

            })


            resolve()
        })
    }


    private IUIApplication_onUIApplicationPRecordChange(record: WB_UIApplicationRow, propertyName: string, propertyValue: any, options: UIApplicationBO) {
        this.onUIApplicationPropertyChanged.next({
            UIApplicationBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    private IUIApplication_onAppReportRecordChange(record: WB_UIAppReportRow, propertyName: string, propertyValue: any, options: UIApplicationBO) {
        this.onAppReportPropertyChanged.next({
            UIApplicationBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    private IUIApplication_onAppModuleRecordChange(record: WB_UIAppModuleRow, propertyName: string, propertyValue: any, options: UIApplicationBO) {
        this.onAppModulePropertyChanged.next({
            UIApplicationBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }


    get onAppReportCollectionChanged(): BehaviorSubject<IUIApplication> {
        return this._AppReportCollectionChanged
    }

    get onAppModuleCollectionChanged(): BehaviorSubject<IUIApplication> {
        return this._AppModuleCollectionChanged
    }


    get onUIApplicationPropertyChanged(): BehaviorSubject<ApplicationChangeEvent> {
        return this._ApplicationPropertyChanged
    }

    get onAppReportPropertyChanged(): BehaviorSubject<AppReportChangeEvent> {
        return this._AppReportPropertyChanged
    }

    get onAppModulePropertyChanged(): BehaviorSubject<AppModuleChangeEvent> {
        return this._AppModulePropertyChanged
    }


    //<editor-fold desc = "App Report Operations">

    addReport(arrReportId: number[]): Promise<WB_UIAppReportRow> {
        const self = this

        return new Promise<WB_UIAppReportRow>((resolve, error) => {
            let arrReportRow: WB_ReportTileRow[] = undefined
            let anAppReportRow: WB_UIAppReportRow = undefined
            let iSort=0
            self.dbContext.findAll(esWB_ReportTile, {
                where: {
                    WB_REPORTTILE_ID: {inq: arrReportId},
                    DELETED_IND: {neq: '1'}
                }
            })
                .then((arrReportRecord: WB_ReportTileRow[]) => {
                    if (_.isEmpty(arrReportRecord)) {
                        // Bad User Role Id...
                        resolve()
                        return
                    }
                    arrReportRow = arrReportRecord
                    let rows = _.orderBy(self.WB_UIApplication.WB_UIAppReportRows, ['SORT_ORDER'], ['desc'])
                    if (rows.length > 0) {
                        iSort = rows[0].cif.SORT_ORDER + 1
                    }
                    return self.dbContext.getNewIds(esWB_UI_App_Report, arrReportRecord.length)
                })
                .then((ids: number[]) => {
                    // create new records
                    for (let i = 0; i < ids.length; i++) {
                        let aNewRow = <WB_UIAppReportRow>self.dbContext.createRecord(esWB_UI_App_Report)
                        aNewRow.setDefaults()
                        anAppReportRow = aNewRow
                        anAppReportRow.cif.WB_UI_APP_REPORT_ID = ids[i]
                        anAppReportRow.cif.WB_UI_APP_ID = self.drWB_UIApplication.cif.WB_UI_APP_ID
                        anAppReportRow.cif.WB_REPORTTILE_ID = arrReportRow[i].cif.WB_REPORTTILE_ID
                        anAppReportRow.WB_ReportTileRow = arrReportRow[i]
                        anAppReportRow.cif.TREE_PATH = arrReportRow[i].cif.TREE_PATH
                        anAppReportRow.cif.SORT_ORDER = self.drWB_UIApplication.WB_UIAppReportRows.length
                        self.dbContext.add(esWB_UI_App_Report, anAppReportRow)
                    }
                    anAppReportRow.setupOnChangeEvents([], self.IUIApplication_onAppReportRecordChange.bind(self), self)
                    self.onAppReportCollectionChanged.next(self)
                    resolve(anAppReportRow)

                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeReport(anAppReportId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            _.forEach(self.drWB_UIApplication.WB_UIAppReportRows, (anAppReportRow: WB_UIAppReportRow) => {
                if (anAppReportRow.isActive && anAppReportRow.cif.WB_UI_APP_REPORT_ID == anAppReportId) {
                    anAppReportRow.markDeleted()
                }
            })
            self.onAppReportCollectionChanged.next(self)
            resolve()
        })
    }

    get AppReports(): WB_UIAppReportRow[] {
        return _.orderBy(this.WB_UIApplication.WB_UIAppReportRows, ['SORT_ORDER'], ['asc'])
    }

    //</editor-fold>


    //<editor-fold desc = "App Module Operations">

    addModule(arrExecId: number[]): Promise<WB_UIAppModuleRow> {
        const self = this

        return new Promise<WB_UIAppModuleRow>((resolve, error) => {

            let arrExecRow: WB_UIAppExecutableRow[] = undefined
            let anAppModuleRow: WB_UIAppModuleRow = undefined
            let iSort: number = 0

            self.dbContext.findAll(esWB_UI_AppExecutable, {
                where: {
                    WB_UI_APPEXEC_ID: {inq: arrExecId},
                    DELETED_IND: {neq: '1'}
                }
            })
                .then((arrExecRecord: WB_UIAppExecutableRow[]) => {
                    if (_.isEmpty(arrExecRecord)) {
                        // Bad App Exec Id...
                        resolve()
                        return
                    }
                    arrExecRow = arrExecRecord

                    let rows = _.orderBy(self.WB_UIApplication.WB_UIAppModuleRows, ['SORT_ORDER'], ['desc'])
                    if (rows.length > 0) {
                        iSort = rows[0].cif.SORT_ORDER + 1
                    }
                    return self.dbContext.getNewIds(esWB_UI_AppModule, arrExecRow.length)
                })
                .then((ids: number[]) => {
                    // create new records
                    for (let i = 0; i < ids.length; i++) {
                        let anExecRow = arrExecRow[i]
                        anAppModuleRow = <WB_UIAppModuleRow>self.dbContext.createRecord(esWB_UI_AppModule)
                        anAppModuleRow.setDefaults()
                        anAppModuleRow.cif.WB_UI_APPMODULE_ID = ids[i]
                        anAppModuleRow.cif.WB_UI_APP_ID = self.drWB_UIApplication.cif.WB_UI_APP_ID
                        anAppModuleRow.cif.WB_UI_APPEXEC_ID = anExecRow.cif.WB_UI_APPEXEC_ID
                        anAppModuleRow.WB_UIAppExecutableRow = anExecRow
                        anAppModuleRow.WB_UIApplicationRow = self.drWB_UIApplication
                        anAppModuleRow.cif.APPMODULE_NAME = anExecRow.cif.APPEXEC_NAME
                        anAppModuleRow.cif.UI5_ICON = anExecRow.cif.UI5_ICON
                        anAppModuleRow.cif.APPMODULE_SHORTNAME = anExecRow.cif.APPEXEC_ID
                        anAppModuleRow.cif.EDIT_ROLES = anExecRow.cif.EDIT_ROLES
                        anAppModuleRow.cif.VIEW_ROLES = anExecRow.cif.VIEW_ROLES
                        anAppModuleRow.cif.LAUNCH_POLICY = self.WB_UIApplication.cif.LAUNCH_POLICY
                        anAppModuleRow.cif.PLACEMENT_POLICY = "Toolbar"
                        anAppModuleRow.cif.MRU_POLICY = "Show"
                        anAppModuleRow.cif.STARTUP_POLICY = self.WB_UIApplication.cif.STARTUP_POLICY
                        anAppModuleRow.cif.RENDER_OPTIONS = anExecRow.cif.RENDER_OPTIONS
                        anAppModuleRow.cif.SORT_ORDER = iSort + i
                        self.dbContext.add(esWB_UI_AppModule, anAppModuleRow)
                        anAppModuleRow.setupOnChangeEvents([], self.IUIApplication_onAppModuleRecordChange.bind(self), self)
                    }
                    self.onAppModuleCollectionChanged.next(self)
                    resolve(anAppModuleRow)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeModule(anAppModuleId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            _.forEach(self.drWB_UIApplication.WB_UIAppModuleRows, (anAppModuleRow: WB_UIAppModuleRow) => {
                if (anAppModuleRow.isActive && anAppModuleRow.cif.WB_UI_APPMODULE_ID == anAppModuleId) {
                    anAppModuleRow.markDeleted()
                }
            })
            self.onAppModuleCollectionChanged.next(self)
            resolve()
        })
    }

    get AppModules(): WB_UIAppModuleRow[] {
        return _.orderBy(this.WB_UIApplication.WB_UIAppModuleRows, ['SORT_ORDER'], ['asc'])
    }

    //</editor-fold>


};
PAEnvironment.registerBusinessObjectClass(UIApplicationBO, "UIApplicationBO", "Application")